export type AmsEnvelope<T> = {
  code: boolean;
  data: T;
  error?: string[];
  description?: string[];
  synthetic?: boolean;
};

const API = "/__archive/api";

export async function apiGet<T>(
  path: string,
  query: Record<string, string | number | boolean | undefined | null> = {},
): Promise<T> {
  const url = new URL(`${API}${path.startsWith("/") ? path : `/${path}`}`, window.location.origin);
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    url.searchParams.set(key, String(value));
  }
  // Historical app always sent subdomain
  if (!url.searchParams.has("subdomain")) {
    url.searchParams.set("subdomain", "cobblehill");
  }
  const response = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error(`API ${response.status}: ${path}`);
  }
  const body = (await response.json()) as AmsEnvelope<T>;
  return body.data;
}

export type Patient = {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  phone?: string;
  birthday?: string;
  profile_url?: string | null;
  account_activated?: boolean;
  last_check_in?: string | null;
  bio?: {
    gender?: string;
    date_of_birth?: string;
    phone_number?: string;
    insurance?: string;
  };
};

export type Provider = {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  title?: string;
  meta_type?: string;
  friendly_type?: string;
  profile_url?: string | null;
  patientable?: boolean;
  requestable?: boolean;
  account_activated?: boolean;
  bio?: { phone_number?: string };
};

export type Activity = {
  id: string;
  key: string;
  activity_type: string;
  owner?: Patient & {
    last_seen?: Record<string, string | null | undefined>;
    bio?: Patient["bio"] & { location?: string };
  };
  owner_id?: string;
  created_at: string;
  trackable?: Record<string, unknown> & {
    provider_id?: string;
    location_id?: string;
    location_name?: string;
    note?: string;
  };
  parameters?: {
    data?: string;
    provider_name?: string;
    provider_id?: string;
    preview?: string;
    location_name?: string;
  };
};

export type Conversation = {
  id: string;
  patient_id: string;
  patient?: Patient;
  unread_count?: number;
  updated_at?: string;
  last_message?: string;
  conversation_participants?: Array<{
    id: string;
    name: string;
    first_name?: string;
    last_name?: string;
    meta_type?: string;
    profile_url?: string | null;
  }>;
  messages_list?: Array<{
    id: string;
    body: string;
    created_at: string;
    user_id: string;
    sender_info?: { name?: string; meta_type?: string; image?: string };
  }>;
};

export type Appointment = {
  id: string;
  patient_id: string;
  provider_id: string;
  start_time: string;
  end_time?: string;
  start_at?: string;
  end_at?: string;
  status: string;
  confirmed?: boolean;
  cancelled?: boolean;
  patient_missed?: boolean;
  foreign_id_type?: string | null;
  note?: string;
  patient_name?: string;
  provider_name?: string;
  appointment_type?: { name?: string; minutes?: number } | string;
  patient?: Patient;
  provider?: Provider;
};

export type Payment = {
  id: string;
  patient_id: string;
  appointment_id?: string;
  amount: number;
  status: string;
  created_at: string;
  description?: string;
};

export type Campaign = {
  id: string;
  name: string;
  status: string;
  subject?: string;
  recipient_count?: number;
  channel?: string;
  created_at?: string;
  updated_at?: string;
  sent_at?: string | null;
  successfully_sent?: boolean;
  types?: { email?: boolean; sms?: boolean };
  content?: {
    email?: { body?: string };
    sms?: { body?: string };
  };
  misc?: {
    type?: string;
    schedule?: Array<{
      num: number;
      unit: string;
      enable_email?: boolean;
      enable_sms?: boolean;
      has_extra_email_text?: boolean;
    }>;
    job_type?: string;
    model?: string;
    verb?: string;
    manually_select?: boolean;
    params?: Record<string, unknown>;
  };
};

export const DEFAULT_AVATAR = "/app/img/icon/avatar.svg";
